import {FC, useMemo, useState} from "react";
import {Box, BoxProps, TextField} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {AdminTable} from "@/app2/shared/components/AdminTable";
import {AdminTableLanguagesFilterSelect} from "@/components/common/table/AdminTableLanguagesFilterSelect";
import {PlaylistDetailDto, PlaylistCourseDto} from "@/app2/shared/data";
import {useGetI18nLocales} from "@/app2/shared/data/repos/settings";
import {
	GridColDef,
	GridFilterInputValue,
	GridFilterItem,
	GridFilterModel,
	GridFilterOperator,
	GridSortModel,
	GridValueGetterParams
} from "@mui/x-data-grid-pro";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import * as pkg from "@mui/x-date-pickers/AdapterDayjs/index.js";
import {formatDateForDisplay, getAppDateInputFormat} from "@/app2/shared/utils/dateManipulation";
const {AdapterDayjs} = pkg;

const titleFilterOperators: GridFilterOperator[] = [
	{value: "contains", label: "Contains", getApplyFilterFn: () => null, InputComponent: GridFilterInputValue},
	{value: "equals", label: "Equals", getApplyFilterFn: () => null, InputComponent: GridFilterInputValue},
	{value: "startsWith", label: "Starts with", getApplyFilterFn: () => null, InputComponent: GridFilterInputValue},
	{value: "endsWith", label: "Ends with", getApplyFilterFn: () => null, InputComponent: GridFilterInputValue},
	{value: "isEmpty", label: "Is empty", getApplyFilterFn: () => null},
	{value: "isNotEmpty", label: "Is not empty", getApplyFilterFn: () => null}
];

const DateFilterInput = (props: any): JSX.Element => (
	<DatePicker
		inputFormat={getAppDateInputFormat()}
		label="Value"
		renderInput={(params) => (
			<TextField
				{...params}
				variant="standard"
				inputProps={{
					...params.inputProps,
					placeholder: getAppDateInputFormat()
				}}
			/>
		)}
		value={props.item.value ? new Date(props.item.value) : null}
		onChange={(newValue: any) => {
			if (newValue && !newValue.isValid()) return;
			props.applyValue({
				...props.item,
				value: newValue ? newValue.toISOString() : null
			});
		}}
	/>
);

const dateFilterOperators: GridFilterOperator[] = [
	{label: "greater than", value: "gt", InputComponent: DateFilterInput, getApplyFilterFn: () => null},
	{label: "less than", value: "lt", InputComponent: DateFilterInput, getApplyFilterFn: () => null},
	{label: "greater than or equal to", value: "ge", InputComponent: DateFilterInput, getApplyFilterFn: () => null},
	{label: "less than or equal to", value: "le", InputComponent: DateFilterInput, getApplyFilterFn: () => null},
	{label: "equals", value: "eq", InputComponent: DateFilterInput, getApplyFilterFn: () => null},
	{label: "not equal to", value: "ne", InputComponent: DateFilterInput, getApplyFilterFn: () => null}
];

const languagesFilterOperators: GridFilterOperator[] = [
	{label: "contains", value: "co", InputComponent: AdminTableLanguagesFilterSelect, getApplyFilterFn: () => null}
];

const toDayMs = (v: unknown): number | null => {
	if (v == null || v === "") return null;
	const d = v instanceof Date ? new Date(v) : typeof v === "string" || typeof v === "number" ? new Date(v) : null;
	if (!d || isNaN(d.getTime())) return null;
	d.setHours(0, 0, 0, 0);
	return d.getTime();
};

const matchesFilter = (row: PlaylistCourseDto, item: GridFilterItem): boolean => {
	if (item.columnField === "title") {
		const cell = (row.title ?? "").toLowerCase();
		const needle = typeof item.value === "string" ? item.value.toLowerCase() : "";
		switch (item.operatorValue) {
			case "contains":   return needle === "" || cell.includes(needle);
			case "equals":     return needle === "" || cell === needle;
			case "startsWith": return needle === "" || cell.startsWith(needle);
			case "endsWith":   return needle === "" || cell.endsWith(needle);
			case "isEmpty":    return cell === "";
			case "isNotEmpty": return cell !== "";
			default:           return true;
		}
	}
	if (item.columnField === "updatedAt") {
		const rowDay = toDayMs(row.updatedAt);
		const filterDay = toDayMs(item.value);
		switch (item.operatorValue) {
			case "eq": return filterDay === null || rowDay === filterDay;
			case "ne": return filterDay === null || rowDay !== filterDay;
			case "gt": return filterDay === null || (rowDay !== null && rowDay > filterDay);
			case "ge": return filterDay === null || (rowDay !== null && rowDay >= filterDay);
			case "lt": return filterDay === null || (rowDay !== null && rowDay < filterDay);
			case "le": return filterDay === null || (rowDay !== null && rowDay <= filterDay);
			default:   return true;
		}
	}
	if (item.columnField === "availableLocales") {
		if (item.operatorValue === "co") {
			const selected = Array.isArray(item.value) ? (item.value as string[]) : [];
			if (!selected.length) return true;
			const codes = row.availableLocales ?? [];
			return codes.some((c) => selected.includes(c));
		}
	}
	return true;
};

interface ICustomProps {
	playlistId: number;
	playlist?: PlaylistDetailDto;
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const CoursesTab: FC<IProps> = ({playlist, ...baseProps}) => {
	const navigate = useNavigate();
	const [sortModel, setSortModel] = useState<GridSortModel>([]);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({items: []});
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const {data: localesData} = useGetI18nLocales();
	const locales = localesData?.data ?? [];

	const handleFilterModelChange = (next: GridFilterModel) => {
		setFilterModel(next);
		setPage(0);
	};

	const handleSortModelChange = (next: GridSortModel) => {
		setSortModel(next);
		setPage(0);
	};

	const handlePageSizeChange = (next: number) => {
		setPageSize(next);
		setPage(0);
	};

	const columns: GridColDef<PlaylistCourseDto>[] = useMemo(() => [
		{
			field: "title",
			headerName: "Title",
			type: "string",
			flex: 1,
			minWidth: 240,
			filterable: true,
			filterOperators: titleFilterOperators
		},
		{
			field: "updatedAt",
			headerName: "Last Modified",
			type: "date",
			flex: 1,
			filterable: true,
			filterOperators: dateFilterOperators,
			valueGetter: (params: GridValueGetterParams<PlaylistCourseDto>) =>
				params.row.updatedAt ? new Date(params.row.updatedAt) : null,
			valueFormatter: (params) => formatDateForDisplay(params.value as Date | null)
		},
		{
			field: "availableLocales",
			headerName: "Supported Languages",
			type: "string",
			flex: 1,
			sortable: false,
			filterable: true,
			filterOperators: languagesFilterOperators,
			valueGetter: (params: GridValueGetterParams<PlaylistCourseDto>) => {
				const codes = params.row.availableLocales;
				if (!codes?.length) return "";
				return codes
					.map((code) => locales.find((l) => l.code === code)?.name ?? code)
					.join(", ");
			}
		}
	], [locales]);

	const rows = playlist?.curriculum ?? [];

	const filteredRows = useMemo(() => {
		if (!filterModel.items.length) return rows;
		const link = filterModel.linkOperator ?? "and";
		return rows.filter((row) => {
			const results = filterModel.items.map((item) => matchesFilter(row, item));
			return link === "or" ? results.some(Boolean) : results.every(Boolean);
		});
	}, [rows, filterModel]);

	const visibleRows = useMemo(() => {
		if (!sortModel.length) return filteredRows;
		const {field, sort} = sortModel[0];
		return [...filteredRows].sort((a, b) => {
			const aVal = String((a as unknown as Record<string, unknown>)[field] ?? "");
			const bVal = String((b as unknown as Record<string, unknown>)[field] ?? "");
			return sort === "desc" ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
		});
	}, [filteredRows, sortModel]);

	const pagedRows = useMemo(
		() => visibleRows.slice(page * pageSize, (page + 1) * pageSize),
		[visibleRows, page, pageSize]
	);

	const handleExport = () => {
		const escape = (v: unknown) => {
			const s = v == null ? "" : String(v);
			return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
		};
		const header = ["Title", "Last Modified", "Supported Languages"];
		const lines = [header.join(",")];
		for (const row of visibleRows) {
			const localeNames = (row.availableLocales ?? [])
				.map((code) => locales.find((l) => l.code === code)?.name ?? code)
				.join(", ");
			lines.push([
				escape(row.title),
				escape(formatDateForDisplay(row.updatedAt)),
				escape(localeNames)
			].join(","));
		}
		const blob = new Blob([lines.join("\n")], {type: "text/csv;charset=utf-8;"});
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${playlist?.title ?? "playlist"}-courses.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	return (
		<Box {...baseProps} sx={{mt: 3, ...(baseProps.sx ?? {})}}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<AdminTable<PlaylistCourseDto>
					rows={pagedRows}
					totalCount={visibleRows.length}
					columns={columns}
					getRowId={(row) => row.id}
					onRowClick={(row) => navigate(`/admin/courses/${row.id}`)}
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					filterModel={filterModel}
					onFilterModelChange={handleFilterModelChange}
					page={page}
					pageSize={pageSize}
					onPageChange={setPage}
					onPageSizeChange={handlePageSizeChange}
					enableAdvancedFilters
					enableColumnFilter
					onExport={handleExport}
					autoHeight
				/>
			</LocalizationProvider>
		</Box>
	);
};
